import '../styles/app.scss';

import { Provider } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getMany } from 'idb-keyval';

import { wrapper } from '@store/store';
import { HistoryProvider } from '@context/History';
import { ParamCheckProvider } from '@context/ParamCheck';
import MainLayout from '@layouts/MainLayout';
import AuthenticationService from '@lib/auth/AuthenticationService';
import { fetchData } from '@lib/helpers';
import setSections from '@actions/setSections';
import { BASE_URL } from '@lib/constants';

import ToastNotification from '@components/ToastNotification';
import useBodyClass from '@hooks/bodyClass';
import { InstallableProvider } from '@context/Installable';

import Head from 'next/head';
import RedHotToast from '@components/RedHotToast';
import { Toaster } from 'react-hot-toast';
import { SerwistProvider } from '@serwist/turbopack/react';

import { I18nProvider, useI18n } from '@lib/i18n';

const updateSiteVisits = `${BASE_URL}/wp-json/edu_api/reporting/prospectus/visits/update`;

function AppContent({ Component, pageProps }) {
	const dispatch = useDispatch();
	const { locale, translations_ready, t } = useI18n();

	const { didSave, isSaving, failedSave, saveTrigger, didLogOut } = useSelector((state) => state.user);

	// I think we should assume we are always online
	const [online, setOnlineStatus] = useState(null);
	const hasRequestedSections = useRef(false);

	const pathClass = useBodyClass();
	useEffect(() => {
		document.body.className = pathClass;
	});

	useEffect(() => {
		// The below logic logs a unique site visit
		const value = `; ${document.cookie}`;
		const parts = value.split(`; previous_visited=`);
		var previous_visited = '';

		if (parts.length === 2) {
			previous_visited = parts.pop().split(';').shift();
		}

		if (!previous_visited) {
			fetch(updateSiteVisits, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
				},
			})
				.then(() => {
					document.cookie = 'previous_visited=true';
				})
				.catch((error) => {
					console.log('There was an error updating the post count');
					console.log(error);
				});
		}
		// Unique site visit logic ends.

		dispatch({ type: 'SET_ONLINE_STATUS', payload: navigator.onLine });

		window.addEventListener('online', () => {
			dispatch({ type: 'SET_ONLINE_STATUS', payload: true });
			setOnlineStatus(true);
		});

		window.addEventListener('offline', () => {
			dispatch({ type: 'SET_ONLINE_STATUS', payload: false });
			setOnlineStatus(false);
		});

		if (navigator.onLine) {
			const auth = new AuthenticationService();
			dispatch(auth.refreshToken(true));

			setInterval(function () {
				dispatch(auth.refreshToken());
			}, 30000);
		}
	}, []);

	useEffect(() => {
		if (!translations_ready) {
			return;
		}

		if (hasRequestedSections.current) {
			return;
		}

		hasRequestedSections.current = true;

		getMany(['sections', 'activeSections', 'inActiveSections'])
			.then(([cacheSections, cacheActive, cacheInactive]) => {
				const sectionObject = {
					fromCache: true,
					updateSections: true,
				};

				// // Local sections, then set in state.
				if (cacheSections) {
					sectionObject.sections = cacheSections;
				}

				// If active locally, set state.
				if ('undefined' !== typeof cacheActive) {
					sectionObject.newData = cacheActive;
				}

				// If active locally, set state.
				if ('undefined' !== typeof cacheInactive) {
					sectionObject.inActiveSections = cacheInactive;
				}

				if (Object.keys(sectionObject).length > 0) {
					dispatch(setSections(sectionObject));
				}

				// If active locally, set state.
				if ('undefined' !== typeof cacheActive) {
					sectionObject.activeSections = cacheActive;
				}

				return sectionObject;
			})
			.then((obj) => getOnlineSectionData(obj));
	}, [translations_ready, locale]);

	const getOnlineSectionData = async (obj) => {
		if (navigator.onLine) {
			const params = new URLSearchParams();

			if (typeof locale === 'string' && /^[a-z]{2,3}(?:-[A-Za-z0-9]{2,8})?$/.test(locale)) {
				params.set('locale', locale);
			}

			const data = await fetchData(
				`${BASE_URL}/wp-json/edu_api/card/sections${params.toString() ? `?${params}` : ''}`
			);

			if (data) {
				const newObject = {
					newData: data,
					updateSections: true,
					validateData: true,
				};

				if (obj.sections) {
					newObject.sections = obj.sections;
				}

				if (obj.activeSections) {
					newObject.existingActive = obj.activeSections;
				}

				if (obj.inActiveSections) {
					newObject.existingInActive = obj.inActiveSections;
				}

				dispatch(setSections(newObject));
			}
		}
	};

	return (
		<MainLayout>
			{isSaving && (
				<RedHotToast
					type="loading"
					toastId="save"
					message={t('toast.saving_prospectus', 'Saving Prospectus')}
					position="bottom-left"
					dismissable={true}
					trigger={null}
				/>
			)}
			{didSave && (
				<RedHotToast
					type="success"
					toastId="save"
					message={t('toast.saved_prospectus', 'Prospectus saved successfully!')}
					position="bottom-left"
					dismissable={true}
					trigger={saveTrigger}
				/>
			)}

			{failedSave && (
				<ToastNotification
					text="Something went wrong when saving your prospectus!"
					type="error"
					fadeOutTime={5000}
					removeToast={() => dispatch({ type: 'FAILED_SAVE', payload: false })}
				/>
			)}

			{didLogOut && (
				<ToastNotification
					text="Logged out successfully!"
					type="success"
					fadeOutTime={5000}
					removeToast={() => dispatch({ type: 'REMOVE_LOGGED_OUT', payload: false })}
				/>
			)}

			<Toaster />
			<Component {...pageProps} />
		</MainLayout>
	);
}

function MyApp({ Component, ...rest }) {
	const { store, props } = wrapper.useWrappedStore(rest);

	return (
		<>
			<Head>
				<meta
					name="viewport"
					content="width=device-width, initial-scale=1.0, user-scalable=yes, maximum-scale=5.0, minimum-scale=1.0"
				/>
			</Head>

			<I18nProvider>
				<InstallableProvider>
					<ParamCheckProvider>
						<HistoryProvider>
							<SerwistProvider swUrl="/serwist/sw.js" options={{ scope: '/', type: 'module' }}>
								<Provider store={store}>
									<AppContent Component={Component} pageProps={props.pageProps} />
								</Provider>
							</SerwistProvider>
						</HistoryProvider>
					</ParamCheckProvider>
				</InstallableProvider>
			</I18nProvider>
		</>
	);
}

export default MyApp;
